/**
 * Matter Report - Query Hooks
 *
 * Custom hooks for fetching Matter Ledger and Statement data using TanStack React Query
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { matterReportApi } from '../api/matterReportApi';
import type {
  MatterLedgerFiltersState,
  MatterStatementFiltersState,
} from '../types';
import type { MatterExportParams } from '../api/matterReportApi';

// ============================================================================
// Query Keys
// ============================================================================

export const MATTER_QUERY_KEYS = {
  ledger: (params: any) => ['matter', 'ledger', params] as const,
  statement: (params: any) => ['matter', 'statement', params] as const,
  export: (params: any) => ['matter', 'export', params] as const,
  matters: (search?: string) => ['matter', 'list', search] as const,
};

// ============================================================================
// Ledger Report Hooks
// ============================================================================

/**
 * Hook for fetching matter ledger transactions
 */
export function useMatterLedger(
  filters: MatterLedgerFiltersState,
  options?: { enabled?: boolean }
) {
  const ledgerParams = {
    matterId: filters.matterId,
    startDate: filters.startDate,
    endDate: filters.endDate,
    page: filters.page || 1,
    limit: filters.limit || 100,
    accountType: 'Trust' as const, // Matter Ledger shows trust accounts
  };

  return useQuery({
    queryKey: MATTER_QUERY_KEYS.ledger(ledgerParams),
    queryFn: () => matterReportApi.getLedger(ledgerParams),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook for exporting matter ledger report
 */
export function useExportMatterLedger() {
  return useMutation({
    mutationFn: (params: MatterExportParams) => matterReportApi.exportLedger({
      ...params,
      accountType: 'Trust', // Matter Ledger export shows trust accounts
    }),
  });
}

/**
 * Hook for syncing matter ledger data from Clio
 */
export function useSyncMatterLedger() {
  return useMutation({
    mutationFn: (matterId: string) => matterReportApi.syncLedger(matterId),
  });
}

// ============================================================================
// Statement Report Hooks
// ============================================================================

/**
 * Hook for fetching matter statement data
 */
export function useMatterStatement(
  filters: MatterStatementFiltersState,
  options?: { enabled?: boolean }
) {
  const statementParams = {
    matterId: filters.matterId,
    startDate: filters.startDate,
    endDate: filters.endDate,
    accountType: 'Operating' as const, // Matter Statement shows operating accounts
  };

  return useQuery({
    queryKey: MATTER_QUERY_KEYS.statement(statementParams),
    queryFn: () => matterReportApi.getStatement(statementParams),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook for exporting matter statement report
 */
export function useExportMatterStatement() {
  return useMutation({
    mutationFn: (params: MatterExportParams) => matterReportApi.exportStatement({
      ...params,
      accountType: 'Operating', // Matter Statement export shows operating accounts
    }),
  });
}

// ============================================================================
// Matters List Hook
// ============================================================================

/**
 * Hook for fetching list of matters with optional search
 * Fetches all matters with pagination by default
 */
export function useMatters(search?: string) {
  return useQuery({
    queryKey: MATTER_QUERY_KEYS.matters(search),
    queryFn: () => matterReportApi.getMatters(search),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

